/**
 * Address Parser Utility
 * Extracts city, country, and postal code from Google Places formatted_address strings
 */

/**
 * Parse a Google Places formatted_address into components.
 * Handles formats like:
 *   "The Farmington Inn & Suites, 827 Farmington Ave, Farmington, CT 06032, United States"
 *   "The Ritz-Carlton, 15 Arlington St, London SW1A 1RJ, UK"
 *   "Hotel Amour, 8 Rue de Navarin, 75009 Paris, France"
 *
 * @param {string} formattedAddress - Google Places formatted_address
 * @returns {{ city: string, country: string, state: string, postalCode: string }}
 */
export function parseFormattedAddress(formattedAddress) {
  if (!formattedAddress) {
    return { city: '', country: '', state: '', postalCode: '' };
  }

  const parts = formattedAddress.split(',').map(p => p.trim()).filter(Boolean);

  if (parts.length < 2) {
    return { city: formattedAddress, country: '', state: '', postalCode: '' };
  }

  // Last part is typically the country
  const country = parts[parts.length - 1];

  // Second-to-last is typically state/province + postal code, or city + postal code
  const secondToLast = parts.length >= 2 ? parts[parts.length - 2] : '';

  // Try to extract postal code and state/city from the second-to-last segment
  // US format: "CT 06032" or "New York, NY 10001"
  // UK format: "London SW1A 1RJ"
  // EU format: "75009 Paris"

  let city = '';
  let state = '';
  let postalCode = '';

  // US-style: "STATE ZIP" e.g. "CT 06032", "NY 10001"
  const usStateZipMatch = secondToLast.match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (usStateZipMatch) {
    state = usStateZipMatch[1];
    postalCode = usStateZipMatch[2];
    // City is the part before state+zip
    city = parts.length >= 3 ? parts[parts.length - 3] : '';
  } else {
    // Try EU-style: "75009 Paris" (postal code before city)
    const euZipMatch = secondToLast.match(/^(\d{4,6})\s+(.+)$/);
    if (euZipMatch) {
      postalCode = euZipMatch[1];
      city = euZipMatch[2];
    } else {
      // Try UK-style or generic: "London SW1A 1RJ" (city then postal code)
      const ukZipMatch = secondToLast.match(/^(.+?)\s+([A-Z0-9]{2,4}\s*[A-Z0-9]{2,4})$/);
      if (ukZipMatch) {
        city = ukZipMatch[1];
        postalCode = ukZipMatch[2];
      } else {
        // No postal code detected - treat the whole segment as city or state
        // If it looks like a US state abbreviation alone
        if (/^[A-Z]{2}$/.test(secondToLast)) {
          state = secondToLast;
          city = parts.length >= 3 ? parts[parts.length - 3] : '';
        } else {
          city = secondToLast;
        }
      }
    }
  }

  // Clean up city - remove any remaining postal codes
  city = city.replace(/\d{4,}/, '').trim();

  return { city, country, state, postalCode };
}

/**
 * Extract address components from a Google Places result.
 * Prefers address_components if available, falls back to parsing formatted_address.
 *
 * @param {object} placeResult - Google Places API result
 * @returns {{ city: string, country: string, state: string, postalCode: string }}
 */
export function extractAddressComponents(placeResult) {
  // If address_components are available, use them (more reliable)
  if (placeResult.address_components && placeResult.address_components.length > 0) {
    const components = placeResult.address_components;
    const getComponent = (type) => {
      const comp = components.find(c => c.types?.includes(type));
      return comp?.long_name || '';
    };
    const getShortComponent = (type) => {
      const comp = components.find(c => c.types?.includes(type));
      return comp?.short_name || '';
    };

    return {
      city: getComponent('locality') || getComponent('sublocality') || getComponent('administrative_area_level_2'),
      country: getComponent('country'),
      state: getShortComponent('administrative_area_level_1'),
      postalCode: getComponent('postal_code')
    };
  }

  // Fall back to parsing formatted_address
  return parseFormattedAddress(placeResult.formatted_address);
}
